import React, { Fragment, useEffect, useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FacebookIcon from "@mui/icons-material/Facebook";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchBar from "@components/inputs/SearchBar/SearchBar";
import InfoCard from "@components/display/InfoCard/InfoCard";
import PaginationControl from "@components/controls/PaginationControl/PaginationControl";
import Button from "@components/buttons/Button/Button";
import QRCodePopup from "@components/modals/QRCodePopup/QRCodePopup";
import { formatDate } from "@util/string";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { MemorialOverview } from "@models/Memorial";
import { deleteMemorial, fetchMemorialContactsExport, fetchMemorials, updateMemorialInfo } from "@services/memorialService";
import { useNavigate } from "react-router-dom";
import { filterByFullName } from "@util/filter";
import DeactivateConfirmationPopup from "@components/modals/DeactivateConfirmationPopup/DeactivateConfirmationPopup";
import { getRootDomain, regionBadge, getRegion } from "@util/domain";
import ActivateConfirmationPopup from "@components/modals/ActivateConfirmationPopup/ActivateConfirmationPopup";
import { Status } from "@models/Common";
import { useAuth } from "@context/auth-context";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import DeleteConfirmationPopup from "@components/modals/DeleteConfirmationPopup/DeleteConfirmationPopup";
import { shareToFacebook } from "@util/share";
import ChooseMemorialTypePopup from "@components/modals/ChooseMemorialTypePopup/ChooseMemorialTypePopup";
import Snackbar from "@components/display/SnackBar/SnackBar";

const RESULTS_PER_PAGE = 8;

const MemorialsPage: React.FC = () => {
    const { authState } = useAuth();
    const { user } = authState;
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [memorials, setMemorials] = useState<MemorialOverview[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedMemorialQrKey, setSelectedMemorialQrKey] = useState("");
    const [showQrCode, setShowQrCode] = useState<boolean>(false);
    const [showDeactivatePopup, setShowDeactivatePopup] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [showActivatePopup, setShowActivatePopup] = useState(false);
    const [selectedMemorialId, setSelectedMemorialId] = useState("");
    const [selectedMemorialName, setSelectedMemorialName] = useState("");
    const [showChooseTypePopup, setShowChooseTypePopup] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "info" | "warning";
    }>({
        open: false,
        message: "",
        severity: "success",
    });

    // Fetch memorials on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await fetchMemorials();
                setMemorials(data);
            } catch (err) {
                setError("Failed to fetch memorials.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, []);

    const hasMemorials = memorials.length > 0;

    // Filter and paginate memorials
    const filteredData = filterByFullName(
        memorials,
        searchTerm,
        user?.userType === "root_admin" ? [(m) => m.pageAdminEmail || ""] : undefined,
    );
    const totalPages = Math.ceil(filteredData.length / RESULTS_PER_PAGE);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * RESULTS_PER_PAGE,
        currentPage * RESULTS_PER_PAGE,
    );

    // Step back a page if deleting/filtering leaves the current page empty
    useEffect(() => {
        if (currentPage > 1 && currentPage > totalPages) {
            setCurrentPage(Math.max(1, totalPages));
        }
    }, [currentPage, totalPages]);

    const handleSetMemorialStatus = async (id: string, status: Status) => {
        try {
            setLoading(true);
            await updateMemorialInfo(id, { status });
            // Update memorial status locally
            setMemorials((prevState) => {
                const newState = [...prevState];
                const index = prevState.findIndex((val) => val.id === id);
                if (index > -1) {
                    newState[index].status = status;
                }
                return newState;
            });
        } catch {
            setError("Failed to set memorial status.");
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = async () => {
        setShowActivatePopup(false);
        await handleSetMemorialStatus(selectedMemorialId, "active");
    };

    const handleDeactivate = async () => {
        setShowDeactivatePopup(false);
        await handleSetMemorialStatus(selectedMemorialId, "deactivated");
    };

    const handleDelete = async () => {
        setShowDeletePopup(false);
        try {
            setLoading(true);
            await deleteMemorial(selectedMemorialId);
            // Update memorial status locally
            setMemorials((prevState) => (
                prevState.filter((val) => val.id !== selectedMemorialId)
            ));
        } catch {
            setError("Failed to delete memorial.");
        } finally {
            setLoading(false);
        }
    };

    const handleExportContacts = async () => {
        try {
            setExportLoading(true);
            const contacts = await fetchMemorialContactsExport();
            const rows = ["name,email"];
            for (const { name, email } of contacts) {
                rows.push(`"${name.replace(/"/g, '""')}","${email.replace(/"/g, '""')}"`);
            }
            const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "memorial_audience.csv";
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            setSnackbar({ open: true, message: "Failed to export contacts.", severity: "error" });
        } finally {
            setExportLoading(false);
        }
    };

    const handleCreate = async () => {
        if (["business_user", "business_admin"].includes(authState.user?.userType || "")) {
            // Additional memorials beyond the monthly allocation are billed automatically -
            // businesses are only blocked here if a prior overage charge failed.
            if (authState.user?.paymentLocked) {
                navigate("/account");
                return;
            }
        }
        if (["standard_user", "root_admin"].includes(authState.user?.userType || "")) {
            setShowChooseTypePopup(true);
        } else {
            navigate("editor?mode=add");
        }
    };

    const handleSelectType = (type: "human" | "pet") => {
        setShowChooseTypePopup(false);
        navigate(`editor?mode=add${type === "pet" ? "&type=pet" : ""}`);
    };

    const handleOrderCandle = async (id: string) => {
        void navigate(`/memorials/purchase-candle?${new URLSearchParams({ id }).toString()}`);
    };

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (memorials.length === 0) {
            return (
                <div className={commonStyles.noContent}>
                    <div style={{ animation: "fadeInScale 1s ease-in-out", textAlign: "center", marginTop: "25px" }}>
                        <h2>Create Your First Memorial</h2>
                        <p style={{ marginTop: "20px", marginBottom: "25px", opacity: 0.9 }}>
                            Honour a loved one with a beautiful memorial page.
                        </p>
                        <Button
                            variant="primary"
                            size="large"
                            onClick={handleCreate}
                        >
                            Create Memorial
                        </Button>
                    </div>
                </div>
            );
        }

        if (paginatedData.length === 0) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>{`No memorials ${searchTerm ? "found" : "yet"}.`}</div>;
        }

        return (
            <div className={commonStyles.itemList}>
                {paginatedData.map((data) => {
                    const url = data.iframeUrl ? `${data.iframeUrl}?memorialId=${data.id}` : `${getRootDomain(data.subdomain)}/memorials/${data.urlSlug || data.id}`;
                    return (
                        <InfoCard
                            key={data.id}
                            title={`${data.firstName} ${data.lastName}`}
                            badge={user?.userType === "root_admin" ? regionBadge(data.region) : undefined}
                            media={{
                                type: "image",
                                src: data.image || "/assets/images/placeholder-person.png",
                            }}
                            coverMedia
                            items={[
                                ...((data.birthDate || data.birthYear) && (data.deathDate || data.deathYear)
                                    ? [`${data.birthDate ? formatDate(data.birthDate) : data.birthYear} - ${data.deathDate ? formatDate(data.deathDate) : data.deathYear}`]
                                    : []),
                                ...(user?.userType === "root_admin" && data.pageAdminEmail ? [`Admin: ${data.pageAdminEmail}`] : []),
                            ]}
                            buttons={[
                                // {
                                //     title: (
                                //         <>
                                //             {/* Share */}
                                //             <ShareIcon
                                //                 // style={{ marginLeft: "3px" }}
                                //                 fontSize="inherit"
                                //             />
                                //         </>
                                //     ),
                                //     variant: "text",
                                //     onClick: () => handleShare(
                                //         `${data.firstName} ${data.lastName} | Forever In Our Hearts`,
                                //         data.isPetMemorial
                                //             ? `Remember the life of ${data.firstName}. Celebrate their memories and share your tributes.`
                                //             : `Remember the life and legacy of ${data.firstName} ${data.lastName}. Celebrate their memories and share your tributes.`,
                                //         url,
                                //     ),
                                // },
                                {
                                    title: (
                                        <>
                                            <FacebookIcon
                                                style={{ marginRight: "3px" }}
                                                fontSize="medium"
                                            />
                                            Share
                                        </>
                                    ),
                                    variant: "text",
                                    onClick: async () => shareToFacebook(url),
                                },
                                {
                                    title: (
                                        <>
                                            <ContentCopyIcon
                                                style={{ marginRight: "3px" }}
                                                fontSize="inherit"
                                            />
                                            Copy Link
                                        </>
                                    ),
                                    variant: "text",
                                    onClick: async () => {
                                        try {
                                            await navigator.clipboard.writeText(url);
                                            setSnackbar({
                                                open: true,
                                                message: "Link copied to clipboard",
                                                severity: "success",
                                            });
                                        } catch {
                                            setSnackbar({
                                                open: true,
                                                message: "Failed to copy link",
                                                severity: "error",
                                            });
                                        }
                                    },
                                },
                                {
                                    title: (
                                        <>
                                            View
                                            <OpenInNewIcon
                                                style={{ marginLeft: "3px" }}
                                                fontSize="inherit"
                                            />
                                        </>
                                    ),
                                    variant: "text",
                                    href: url,
                                    newTab: true,
                                },
                                ...((data.isFreeVersion)
                                    ? [
                                        {
                                            title: "Upgrade",
                                            variant: "submit" as const,
                                            onClick: () => navigate(`/memorials/purchase-memorial?${new URLSearchParams({
                                                memorialId: data.id,
                                                type: data.isPetMemorial ? "pet" : "traditional",
                                            }).toString()}`),
                                        },
                                    ]
                                    : []),
                                {
                                    title: "Edit",
                                    variant: "secondary",
                                    to: `editor?${new URLSearchParams({ mode: "edit", id: data.id }).toString()}`,
                                },
                                // {
                                //     title: data.status === "active" ? "Deactivate" : "Activate",
                                //     variant: data.status === "active" ? "cancel" : "primary",
                                //     onClick: () => {
                                //         setSelectedMemorialId(data.id);
                                //         setSelectedMemorialName(`${data.firstName} ${data.lastName}`);
                                //         if (data.status === "active") {
                                //             setShowDeactivatePopup(true);
                                //         } else {
                                //             setShowActivatePopup(true);
                                //         }
                                //     },
                                // },
                                {
                                    title: <DeleteOutlineIcon fontSize="small" />,
                                    variant: "cancel" as const,
                                    onClick: () => {
                                        setSelectedMemorialId(data.id);
                                        setSelectedMemorialName(`${data.firstName} ${data.lastName}`);
                                        setShowDeletePopup(true);
                                    },
                                },
                            ]}
                            bottomButtons={[
                                {
                                    title: "QR Code",
                                    variant: "primary",
                                    onClick: () => {
                                        setSelectedMemorialId(data.id);
                                        setSelectedMemorialQrKey(data.qrKey);
                                        setSelectedMemorialName(`${data.firstName} ${data.lastName}`);
                                        setShowQrCode(true);
                                    },
                                },
                                {
                                    title: "Service QR Sheet",
                                    variant: "primary" as const,
                                    onClick: () => navigate(`service-sheet?${new URLSearchParams({ id: data.id }).toString()}`),
                                },
                                {
                                    title: "Guest Book",
                                    variant: "primary" as const,
                                    disabled: data.isFreeVersion,
                                    onClick: () => navigate(`guest-book?${new URLSearchParams({ id: data.id }).toString()}`),
                                },
                                {
                                    title: "Export PDF",
                                    variant: "primary" as const,
                                    disabled: data.isFreeVersion,
                                    onClick: () => navigate(`export-pdf?${new URLSearchParams({ id: data.id }).toString()}`),
                                },
                                // Forever Candles ship within Australia only
                                ...(getRegion() === "au" ? [{
                                    title: "Forever Candle",
                                    variant: "submit" as const,
                                    onClick: () => handleOrderCandle(data.id),
                                }] : []),
                                ...((data.businessId && (data.setAdminStatus !== "set") && (user?.userType !== "standard_user"))
                                    ? [
                                        {
                                            title: data.setAdminStatus === "not set" ? "Set Family Admin" : "Change Family Admin",
                                            variant: data.setAdminStatus === "not set" ? "tertiary" as const : "secondary" as const,
                                            onClick: () => navigate(`set-admin?${new URLSearchParams({
                                                id: data.id,
                                                mode: data.setAdminStatus === "not set" ? "set" : "change",
                                            }).toString()}`),
                                        },
                                    ]
                                    : []),
                                {
                                    title: `Moderate Tributes${data.tributesForModeration > 0 ? ` (${data.tributesForModeration})` : ""}`,
                                    variant: "secondary",
                                    disabled: data.isFreeVersion,
                                    onClick: () => navigate(`tribute-moderation?${new URLSearchParams({ id: data.id }).toString()}`),
                                },
                            ]}
                            bottomText={
                                data.tributesForModeration
                                    ? `${data.tributesForModeration} Tribute${data.tributesForModeration > 1 ? "s" : ""} Awaiting Review`
                                    : undefined
                            }
                            bottomTextLink={`tribute-moderation?${new URLSearchParams({ id: data.id }).toString()}`}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div className={commonStyles.container}>
            <div className={commonStyles.headerContainer}>
                {hasMemorials && (
                    <h1>Forever Memorials</h1>
                )}
                {!loading && !error && hasMemorials && (
                    <div className={commonStyles.buttonGroup}>
                        {["business_admin", "business_user"].includes(user?.userType || "") && (
                            <Button variant="secondary" size="medium" onClick={handleExportContacts} disabled={exportLoading}>
                                {exportLoading ? "Exporting..." : "Export Audience"}
                            </Button>
                        )}
                        <Button variant="primary" size="medium" onClick={handleCreate}>
                            Create
                        </Button>
                    </div>
                )}
            </div>
            {!loading && hasMemorials && (
                <div className={commonStyles.searchBar}>
                    <SearchBar value={searchTerm} onChange={(val) => setSearchTerm(val)} />
                </div>
            )}
            {renderContent()}
            {totalPages > 1 && (
                <div className={commonStyles.pagination}>
                    <PaginationControl
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                </div>
            )}

            <QRCodePopup
                visible={showQrCode}
                memorialId={selectedMemorialId}
                qrKey={selectedMemorialQrKey}
                downloadFileName={`${selectedMemorialName.replaceAll(" ", "_")}_QRcode`}
                onClose={() => setShowQrCode(false)}
            />
            <ActivateConfirmationPopup
                visible={showActivatePopup}
                onConfirm={handleActivate}
                onCancel={() => setShowActivatePopup(false)}
            />
            <DeactivateConfirmationPopup
                visible={showDeactivatePopup}
                onConfirm={handleDeactivate}
                onCancel={() => setShowDeactivatePopup(false)}
            />
            <DeleteConfirmationPopup
                visible={showDeletePopup}
                onConfirm={handleDelete}
                onCancel={() => setShowDeletePopup(false)}
            />
            <ChooseMemorialTypePopup
                visible={showChooseTypePopup}
                onClose={() => setShowChooseTypePopup(false)}
                onSelect={handleSelectType}
            />

            <Snackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </div>
    );
};

export default MemorialsPage;
